import { type SubmitEvent as ReactSubmitEvent, Fragment, memo, useState, type PropsWithChildren } from "react";
import { type IpFilter, IpFilterType } from "Interfaces";
import "Components/ConfigLayout/Network/SecuritySettings/FilterManagementModal/FilterManagementModal.less";
import {
	Button, ButtonTypes, Input, type SelectOption, Modal, Select
} from "@clintonelec/react-storybook";
import { ipv4Regex } from "@clintonelec/typescriptutils";
import { NotificationType, notify } from "src/Notifications";

type FilterManagementModalProps = {
	allFilters?: IpFilter[];
	filter?: IpFilter;
	filterActionCallback: (filter: IpFilter | string, oldFilterAddress?: string) => void;
};

type FilterManagementForm = {
	startAddress: HTMLInputElement;
	endAddress?: HTMLInputElement;
} & HTMLFormElement;

const ipTypeOptions: SelectOption[] = [
	{
		label: "Single Address",
		value: IpFilterType.SINGLE_ADDRESS
	},
	{
		label: "Address Range",
		value: IpFilterType.ADDRESS_RANGE
	}
];

function FilterManagementModal(props: PropsWithChildren<FilterManagementModalProps>) {
	const { allFilters, children, filter, filterActionCallback } = props;
	const [ visible, setVisible ] = useState(false);
	const [ submitted, setSubmitted ] = useState(false);
	const [ ipType, setIpType ] = useState(filter?.type ?? IpFilterType.SINGLE_ADDRESS);

	const handleVisibilityChange = (newVisibility: boolean) => {
		setVisible(newVisibility);
		setIpType(filter?.type ?? IpFilterType.SINGLE_ADDRESS);
		setSubmitted(false);
	};

	const handleFormSubmit = (event: ReactSubmitEvent<FilterManagementForm>) => {
		setSubmitted(true);
		event.preventDefault();
		event.stopPropagation();

		if (event.currentTarget?.checkValidity()) {
			const {
				startAddress: { value: newFilterStartAddress },
				endAddress
			} = event.currentTarget;

			const newFilterEndAddress = endAddress?.value ?? "";
			const formFilter: IpFilter = {
				addresses: [ newFilterStartAddress, newFilterEndAddress ],
				type: ipType
			};

			const startAddressIndex = allFilters?.findIndex(current =>
				current.addresses[ 0 ] === newFilterStartAddress
			);

			const endAddressIndex = newFilterEndAddress
				? allFilters?.findIndex(current => current.addresses[ 1 ] === newFilterEndAddress)
				: -1;

			if ((startAddressIndex && startAddressIndex >= 0) || (endAddressIndex && endAddressIndex >= 0)) {
				notify(NotificationType.ERROR, "That filter already exists.");

				return;
			}

			if (filter) {
				if (filter !== formFilter) {
					filterActionCallback(formFilter, filter.addresses[ 0 ]);
				}
			} else {
				filterActionCallback(formFilter);
			}

			setVisible(false);
		}
	};

	const validateAddress = (value?: string | number | readonly string[]) => {
		if (typeof value !== "string") {
			return false;
		}

		return ipv4Regex.test(value);
	};

	const handleIpTypeSelect = (newIpType: string | null) => {
		if (!newIpType) {
			return;
		}

		setIpType(newIpType as IpFilterType);
	};

	const endAddressField = () => {
		if (ipType === IpFilterType.SINGLE_ADDRESS) {
			return;
		}

		return (
			<Fragment>
				<span>~</span>
				<div>
					<label>End Address</label>
					<Input
						defaultValue={ filter?.addresses[ 1 ] }
						name="endAddress"
						noValidate={ !submitted }
						required
						validator={ validateAddress }
						validityMessage="A valid IP Address is required."
					/>
				</div>
			</Fragment>
		);
	};

	const modalContent = (
		<form
			className="filter-management-content"
			noValidate
			onSubmit={ handleFormSubmit }
		>
			<label>Address</label>
			<Select
				allowClear={ false }
				value={ ipType }
				onSelect={ handleIpTypeSelect }
				options={ ipTypeOptions }
			/>
			<div className="address-area">
				<div>
					<label>{ ipType === IpFilterType.SINGLE_ADDRESS ? "IP Address" : "Start Address" }</label>
					<Input
						defaultValue={ filter?.addresses[ 0 ] }
						name="startAddress"
						noValidate={ !submitted }
						required
						validator={ validateAddress }
						validityMessage="A valid IP Address is required."
					/>
				</div>
				{ endAddressField() }
			</div>
			<div className="submit-button">
				<Button type={ ButtonTypes.TERTIARY }>
					Submit
				</Button>
			</div>
		</form>
	);

	return (
		<Modal
			modalContent={ modalContent }
			onVisibilityChange={ handleVisibilityChange }
			title={ filter ? "Edit IP Address" : "Add IP Address" }
			visible={ visible }
			width={ 400 }
		>
			{ children }
		</Modal>
	);
}

export default memo(FilterManagementModal);
